import { addDays, format, isValid, parse } from "date-fns";
import { WorkingHour, WorkingHourForm } from "./WorkingHoursDrawer.types";
import { WorkingHourFragment } from "../Availability-generated.hooks";
import { DayOfTheWeek } from "../../../graphql/graphql-generated.types";

export const START_TIME_DEFAULT = "09:00";
export const END_TIME_DEFAULT = "17:00";
const DAY_IN_MINUTES = 24 * 60;

const weekDays = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export const workingHoursDefault: WorkingHour[] = weekDays.map((day) => ({
  enabled: false,
  dayOfTheWeek: day,
  startTime: START_TIME_DEFAULT,
  endTime: END_TIME_DEFAULT,
}));

export function getNextWorkingHours({
  startTime,
  endTime,
  dayOfTheWeek,
}: {
  dayOfTheWeek?: string;
  startTime?: string;
  endTime?: string;
}) {
  if (startTime === undefined || endTime === undefined) {
    return {
      enabled: true,
      dayOfTheWeek,
      startTime: undefined,
      endTime: undefined,
    };
  }
  const endTimeInMin = convertToMinutes(endTime);
  let nextStartTimeInMin = endTimeInMin + 60;
  let nextEndTimeInMin = nextStartTimeInMin + 60;
  if (nextStartTimeInMin >= DAY_IN_MINUTES) {
    nextStartTimeInMin = undefined;
    nextEndTimeInMin = undefined;
  }
  if (nextEndTimeInMin !== undefined && nextEndTimeInMin >= DAY_IN_MINUTES) {
    nextEndTimeInMin = 0;
  }
  if (endTime === "00:00") {
    nextStartTimeInMin = undefined;
    nextEndTimeInMin = undefined;
  }
  const nextStartTime = convertTo24Hours(nextStartTimeInMin);
  const nextEndTime = convertTo24Hours(nextEndTimeInMin);
  return {
    enabled: true,
    dayOfTheWeek: dayOfTheWeek,
    startTime: nextStartTime,
    endTime: nextEndTime,
  };
}

function convertToMinutes(time: string): number {
  const [hour, minute] = time.split(":");
  return parseInt(hour) * 60 + parseInt(minute);
}

function convertTo24Hours(minutes?: number) {
  if (minutes === undefined) {
    return undefined;
  }
  const hour = String(Math.floor(minutes / 60)).padStart(2, "0");
  const minute = String(minutes % 60).padStart(2, "0");
  return `${hour}:${minute}`;
}

export const validateWorkingHours = (
  workingHours: Partial<WorkingHour>[],
  periodIndex: number,
) => {
  const wh = workingHours[periodIndex];

  // is enabled
  if (!wh.enabled) {
    return;
  }

  // required fields
  if (!wh.startTime) {
    return "Start time is required";
  }
  if (!wh.endTime) {
    return "End time is required";
  }

  // validate start
  const startDate = parse(wh.startTime, "HH:mm", new Date());
  if (!isValid(startDate)) {
    return "Start time is invalid";
  }

  // validate end
  let endDate = parse(wh.endTime, "HH:mm", new Date());
  if (wh.endTime === "00:00") {
    endDate = addDays(endDate, 1);
  }
  if (!isValid(endDate)) {
    return "End time is invalid";
  }

  // start time must be before end time
  if (startDate >= endDate) {
    return "Start time must be before end time";
  }

  // periods can't overlap
  const previousOfTheSameDay = workingHours.filter(
    (item, index) =>
      item.dayOfTheWeek === wh.dayOfTheWeek && index < periodIndex,
  );
  const overlapping = previousOfTheSameDay.some((item) =>
    isOverlapping(wh, item),
  );
  if (overlapping) {
    return "Periods can't overlap";
  }

  // start time equal to any end time
  const sameStart = previousOfTheSameDay.some(
    (item) => wh.startTime === item.endTime,
  );
  if (sameStart) {
    return "Start time can't be the same as any end time";
  }
};

export function isOverlapping(
  a: Partial<WorkingHour>,
  b: Partial<WorkingHour>,
) {
  if (!a || !b) {
    return false;
  }
  if (!a.enabled || !b.enabled) {
    return false;
  }
  if (a.dayOfTheWeek !== b.dayOfTheWeek) {
    return false;
  }
  const aStartTime = convertTimeStringToDate(a.startTime);
  const aEndTime = convertTimeStringToDate(a.endTime);
  const bStartTime = convertTimeStringToDate(b.startTime);
  const bEndTime = convertTimeStringToDate(b.endTime);
  if (!aStartTime || !aEndTime || !bStartTime || !bEndTime) {
    return false;
  }
  return aStartTime < bEndTime && aEndTime > bStartTime;
}

/**
 * Convert time string to date for comparison
 * @example convertTimeStringToDate("13:45")
 */
function convertTimeStringToDate(time?: string) {
  return time ? new Date(`2000-01-01T${time}`) : null;
}

export function isLastMidNight(
  dayOfTheWeek: string,
  workingHours?: Partial<WorkingHour>[],
) {
  if (!workingHours) {
    return false;
  }
  const filtered = workingHours.filter(
    (item) => item.dayOfTheWeek === dayOfTheWeek,
  );
  if (filtered.length === 0) {
    return false;
  }
  return filtered[filtered.length - 1].endTime === "00:00";
}

export function convertToFormValues(
  data?: WorkingHourFragment[],
): WorkingHourForm {
  const workingHours: WorkingHour[] = [];
  workingHoursDefault.forEach((item) => {
    const { dayOfTheWeek } = item;
    const found = data?.filter(
      (workingHour) => workingHour.dayOfTheWeek === dayOfTheWeek,
    );
    if (found?.length) {
      found.forEach((itemFound) => {
        workingHours.push({
          enabled: true,
          dayOfTheWeek: itemFound.dayOfTheWeek,
          startTime: convertTimeTo24(itemFound.startTime),
          endTime: convertTimeTo24(itemFound.endTime),
        });
      });
    } else {
      workingHours.push(item);
    }
  });
  return {
    workingHours,
  };
}

export function convertToSaveVariables(
  userUuid: string,
  values: WorkingHourForm,
) {
  return {
    input: {
      userUuid,
      workingHours: values.workingHours
        .filter((item) => item.enabled)
        .sort(compareWorkingHours)
        .map((item) => ({
          dayOfTheWeek: item.dayOfTheWeek as DayOfTheWeek,
          startTime: convertTimeTo12(item.startTime!),
          endTime: convertTimeTo12(item.endTime!),
        })),
    },
  };
}

function convertTimeTo24(time: string) {
  const parsed = parse(time, "hh:mm a", new Date());
  return format(parsed, "HH:mm");
}

function convertTimeTo12(time: string) {
  const parsed = parse(time, "HH:mm", new Date());
  return format(parsed, "hh:mm a");
}

export function findAllSameDayAndSort(
  workingHours: Partial<WorkingHour>[],
  dayOfTheWeek: string,
) {
  return workingHours!
    .filter((item) => item.dayOfTheWeek === dayOfTheWeek)
    .sort(compareWorkingHours);
}

function compareWorkingHours(a: Partial<WorkingHour>, b: Partial<WorkingHour>) {
  if (!a?.startTime || !b?.startTime) {
    return 0;
  }
  const timeA = parse(a?.startTime, "HH:mm", new Date()).getTime();
  const timeB = parse(b?.startTime, "HH:mm", new Date()).getTime();
  return timeA - timeB;
}
